// ── Galaxy State Types ──
// Used by the galaxy map view between encounters.

export type GameMode = "galaxy_map" | "bridge" | "warp_transition" | "replay";

export type FactionRelationStatus =
  | "allied"
  | "friendly"
  | "neutral"
  | "cold_war"
  | "hostile"
  | "war";

export type TradeRouteStatus = "active" | "embargoed" | "disrupted";

export type FactionPresenceLevel = "heavy" | "moderate" | "light" | "none";

export type MissionType =
  | "patrol"
  | "escort"
  | "investigate"
  | "diplomatic"
  | "trade"
  | "rescue";

export type MissionUrgency = "routine" | "priority" | "urgent";

export type EventVisibility = "public" | "faction_only";

// ── Faction colors ──

export const FACTION_COLORS: Record<string, string> = {
  "Stellar Coalition": "#4a8eff",
  "Velan Dominion": "#ef4444",
  "Neutral": "#888888",
  "Unaffiliated": "#666666",
  "Traders Guild": "#f59e0b",
  "Frontier Alliance": "#34d399",
};

// ── Galaxy state shape ──

export interface GalaxySystem {
  id: string;
  name: string;
  position: { x: number; y: number };
  spectralClass: string;
  region: string;
  description: string;
  faction: string | null;
  bodies: number;
  stations: number;
  notableFeatures: string[];
}

export interface FactionRelation {
  factionA: string;
  factionB: string;
  status: FactionRelationStatus;
  tension: number; // 0-100
}

export interface TradeRoute {
  systemA: string;
  systemB: string;
  status: TradeRouteStatus;
}

export interface FactionPresence {
  systemId: string;
  factionId: string;
  level: FactionPresenceLevel;
}

export interface GalaxyEvent {
  id: string;
  timestamp: string;
  systemId: string;
  headline: string;
  description: string;
  factionsInvolved: string[];
  visibility: EventVisibility;
  faction?: string; // which faction can see faction_only events
}

export interface GalaxyMission {
  id: string;
  systemId: string;
  title: string;
  description: string;
  faction: string;
  type: MissionType;
  urgency: MissionUrgency;
}

export interface PlayerState {
  shipName: string;
  shipClass: string;
  faction: string;
  location: string; // current system ID
  hullCondition: string;
  shieldsStatus: string;
  systems: Record<string, string>;
  crew: { name: string; role: string; status: string }[];
  factionStanding: Record<string, number>; // -100 to 100
}

export interface GalaxyState {
  systems: GalaxySystem[];
  factionRelations: FactionRelation[];
  tradeRoutes: TradeRoute[];
  factionPresence: FactionPresence[];
  recentEvents: GalaxyEvent[];
  availableMissions: GalaxyMission[];
  player: PlayerState;
}
